import { cloneDeep } from 'lodash'
import { RestRequest, RestContext, ResponseFunction } from 'msw'
import {
  addNewEntityDataToEntityListData,
  updateEntityListDataMultipleFromUpdatedEntityData,
} from './mockDataCommon'
import { findVersionConflict, versionConflictBody } from './versionConflict'
import {
  findSupplierProductAndItemConflict,
  supplierProductAndItemConflictBody,
} from './supplierProductAndItemConflict'
import { storeEntityDataListToLocalForage } from './mockDataStorageManager'
import {
  ENTITY_ACTIONS_REGISTRY,
  getEntityActionsRegistration,
  type IEntityActionsRegistration,
} from './entityActionsRegistry'
import { createMockOperation } from '../api/operations'
import type { IEntityDataItem, TEntityName } from '~/types/models'

const mn = '[api/msw/modules/mockEntityActions.ts]'

interface IEntityAction {
  action: 'update' | 'create'
  entity: TEntityName
  rows: Partial<IEntityDataItem>[]
}

/**
 * The `/api/transactions` endpoint: several typed writes, applied atomically.
 *
 * Changing a kit's options can supersede rows, revive hidden ones and create
 * new ones at once. That used to be a PATCH followed by a separate POST, and
 * nothing could put the first back if the second failed — the client had to
 * choose which broken state was less harmful, and settled on leaving a visible
 * gap rather than a silent duplicate. This is the endpoint that makes the
 * question moot.
 *
 * ⚠ THE MOCK MUST BE ABLE TO PRODUCE THE FAILURES THIS EXISTS TO PREVENT.
 * `postOrderOverview` is the closest precedent structurally and the wrong model
 * for the point: it writes four entity types in one request and neither rolls
 * back nor checks a version, so a mock built in its image could never show that
 * atomicity was broken. Hence the validation pass below, and the snapshots.
 *
 * It serves `POST /api/transactions`, which accepts any mix of REGISTERED
 * entities. The per-collection `/api/<silo>msservices/actions` routes it once
 * also served are retired (cynque-api-server #1627 phase B), and are gone
 * from the mock too: a mock route the server does not have is a request that
 * works in dev and 404s in production.
 */
export async function handleEntityActions(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext,
  { operationType }: { operationType: string }
) {
  const fn = `${mn} handleEntityActions |`
  try {
    const body = await req.json()
    const actions: IEntityAction[] = body?.actions

    if (!Array.isArray(actions)) {
      return res(
        ctx.status(400),
        ctx.json({
          statusCode: 400,
          message: `${fn} body has no actions array.`,
        })
      )
    }

    for (const [actionIndex, action] of actions.entries()) {
      if (action?.action !== 'update' && action?.action !== 'create') {
        return res(
          ctx.status(422),
          ctx.json({
            statusCode: 422,
            message: `${fn} action ${actionIndex} has unknown action '${action?.action}'.`,
          })
        )
      }
      // ⚠ THE BOUNDARY IS ENFORCED, NOT IGNORED. The limit is the REGISTRY: an
      // entity nobody registered is a 422 rather than a write that silently
      // goes nowhere, as on the real server.
      if (!getEntityActionsRegistration(action.entity)) {
        return res(
          ctx.status(422),
          ctx.json({
            statusCode: 422,
            message:
              `${fn} action ${actionIndex} is for '${action.entity}', which is` +
              ` not registered for /api/transactions. Registered:` +
              ` ${Object.keys(ENTITY_ACTIONS_REGISTRY).join(', ')}.`,
            at: { actionIndex },
          })
        )
      }
      if (!Array.isArray(action.rows)) {
        return res(
          ctx.status(422),
          ctx.json({
            statusCode: 422,
            message: `${fn} action ${actionIndex} has no rows array.`,
          })
        )
      }
    }

    // One list per distinct entity in the body, fetched once. `getListData`
    // returns the live shared array, so fetching it twice for two actions on
    // the same entity would hand back the same reference anyway — but the
    // snapshot below has to be taken exactly once per list, which this makes
    // true by construction.
    const registrations = new Map<TEntityName, IEntityActionsRegistration>()
    const listsByEntity = new Map<TEntityName, IEntityDataItem[]>()
    for (const action of actions) {
      if (listsByEntity.has(action.entity)) {
        continue
      }
      const registration = getEntityActionsRegistration(action.entity)
      if (!registration) {
        // Unreachable for the collection-less route (checked above) and for a
        // per-collection route whose own entity is registered. A route wired
        // to an unregistered entity is a programming error, not a client one.
        return res(
          ctx.status(500),
          ctx.json({
            statusCode: 500,
            message: `${fn} '${action.entity}' has no /api/transactions registration.`,
          })
        )
      }
      registrations.set(action.entity, registration)
      listsByEntity.set(action.entity, await registration.getEntityListDataFn())
    }

    // ⚠ VALIDATE EVERY ACTION BEFORE WRITING ANY ROW. Interleaving validation
    // with writing would leave earlier actions applied when a later one fails,
    // which is the exact partial state this endpoint exists to make impossible —
    // and it is what the two-request flow it replaces could not avoid.
    for (const [actionIndex, action] of actions.entries()) {
      const listData = listsByEntity.get(action.entity) as IEntityDataItem[]
      // The server's validate_*service trigger, on every msService table.
      if (action.entity.endsWith('MSService')) {
        const bothSet = findSupplierProductAndItemConflict({
          submittedRows: action.rows,
          listData: action.action === 'update' ? listData : undefined,
        })
        if (bothSet) {
          return res(
            ctx.status(409),
            ctx.json({
              ...supplierProductAndItemConflictBody(bothSet),
              at: { actionIndex, rowIndex: bothSet.index },
            })
          )
        }
      }
      if (action.action === 'update') {
        for (const [rowIndex, row] of action.rows.entries()) {
          if (!listData.some((stored) => stored.id === row.id)) {
            return res(
              ctx.status(422),
              ctx.json({
                statusCode: 422,
                message: `${fn} no '${action.entity}' with id '${row.id}' to update.`,
                at: { actionIndex, rowIndex },
              })
            )
          }
        }
        const conflict = findVersionConflict({
          listData,
          submittedRows: action.rows,
        })
        if (conflict) {
          return res(
            ctx.status(409),
            ctx.json({
              ...versionConflictBody(conflict),
              at: { actionIndex, rowIndex: conflict.index },
            })
          )
        }
      } else {
        for (const [rowIndex, row] of action.rows.entries()) {
          if (!row.id) {
            return res(
              ctx.status(422),
              ctx.json({
                statusCode: 422,
                message: `${fn} a row to create has no id.`,
                at: { actionIndex, rowIndex },
              })
            )
          }
          if (listData.some((stored) => stored.id === row.id)) {
            return res(
              ctx.status(409),
              ctx.json({
                statusCode: 409,
                message: `${fn} a '${action.entity}' with id '${row.id}' already exists.`,
                id: row.id,
                at: { actionIndex, rowIndex },
              })
            )
          }
        }
      }
    }

    // ⚠ BOUNDED ROLLBACK, AND THE BOUNDARY IS WORTH KNOWING. This restores
    // every list the request touches if a write throws midway. It does NOT roll
    // back the cross-entity propagation the postProcess functions do into the
    // masterService and silo stores — that is derived data, recomputed on the
    // next write. Tests for atomicity must therefore assert on the rows, not on
    // rolled-up amounts.
    const snapshots = new Map<TEntityName, IEntityDataItem[]>()
    for (const [entity, listData] of listsByEntity) {
      snapshots.set(entity, cloneDeep(listData))
    }

    const resultActions: Array<{
      action: string
      entity: TEntityName
      rows: unknown
    }> = []
    try {
      for (const action of actions) {
        const registration = registrations.get(
          action.entity
        ) as IEntityActionsRegistration
        if (action.rows.length === 0) {
          resultActions.push({ ...action, rows: [] })
          continue
        }
        // 🔑 entityKeyBaseOverride ON EVERY WRITE. Without it persistence keys
        // off the URL, which on `/api/transactions` names no entity at all — so all
        // four lists would be written into one slot and clobber each other.
        if (action.action === 'update') {
          const rows = await updateEntityListDataMultipleFromUpdatedEntityData({
            req,
            entityKeyBaseOverride: registration.storageKeyBase,
            updatedEntityDataMultiple: action.rows,
            getEntityListDataFn: registration.getEntityListDataFn,
            postProcessOnceForAllFn: registration.propagateChangesFn,
          })
          resultActions.push({ action: 'update', entity: action.entity, rows })
        } else {
          const rows = await addNewEntityDataToEntityListData({
            req,
            entityKeyBaseOverride: registration.storageKeyBase,
            newEntityData: action.rows as any,
            getEntityListDataFn: registration.getEntityListDataFn,
            postProcessOnceForAllFn: registration.propagateAdditionsFn,
          })
          resultActions.push({ action: 'create', entity: action.entity, rows })
        }
      }
    } catch (e) {
      console.error(`${fn} a write failed. Restoring the pre-request lists.`, e)
      for (const [entity, snapshot] of snapshots) {
        const registration = registrations.get(
          entity
        ) as IEntityActionsRegistration
        const current: IEntityDataItem[] =
          await registration.getEntityListDataFn()
        // splice rather than reassign: callers hold the array reference.
        current.splice(0, current.length, ...snapshot)
        await storeEntityDataListToLocalForage({
          req,
          entityKeyBaseOverride: registration.storageKeyBase,
          listData: current,
        })
      }
      throw e
    }

    // ⚠ The rows are EXACTLY what the write helpers returned — the whole row as
    // sent, with hrid and version interpolated. That is what the live server
    // does (see the notes in saveEntityData.ts) and what every other mock write
    // already produces. Picking fields here would make this one endpoint
    // narrower than both, for no gain.
    const resultData = { actions: resultActions }

    // ⚠ `{ data }` ON THE SYNC PATH, UNWRAPPED ON THE ASYNC ONE, so the client
    // sees the same object either way. httpPost returns `responseBody.data`,
    // while pollOperation returns `result.data` and createMockOperation supplies
    // that wrapper itself. postOrderOverview gets this wrong — its 201 body is
    // bare, so its sync path yields undefined, invisible only because its caller
    // always sends an idempotency key.
    if (req.headers.get('Idempotency-Key')) {
      const operationId = createMockOperation(operationType, resultData)
      return res(
        ctx.status(202),
        ctx.json({
          data: {
            operation_id: operationId,
            status: 'pending',
            operation_type: operationType,
          },
        })
      )
    }
    // 200, not 201, as the real server answers (#1627): a transaction has no
    // single created resource to name.
    return res(ctx.status(200), ctx.json({ data: resultData }))
  } catch (e) {
    console.debug(`${fn} error...`, e)
    return res(
      ctx.status(500),
      ctx.json({ statusCode: 500, message: `${fn} handleEntityActions failed` })
    )
  }
}
